import { flip, type FloatingContext, offset, shift, type UseFloatingReturn, useListNavigation, useTypeahead } from '@floating-ui/react'
import { type MutableRefObject, useLayoutEffect, useRef, useState } from 'react'

import type { DropdownMenuSearch } from './DropdownSearch'

/**
 * The floating-ui setup a root menu and a submenu share, so a nested panel is positioned and navigated
 * like the one it opened out of.
 */

export const DEFAULT_SIDE_OFFSET = 4
export const DEFAULT_COLLISION_PADDING = 8

type DropdownMiddlewareOptions = {
    sideOffset?: number
    alignOffset?: number
    avoidCollisions?: boolean
    collisionPadding?: number
}

export const dropdownMiddleware = ({
    sideOffset = DEFAULT_SIDE_OFFSET,
    alignOffset = 0,
    avoidCollisions = true,
    collisionPadding = DEFAULT_COLLISION_PADDING,
}: DropdownMiddlewareOptions) => [
    offset({ mainAxis: sideOffset, alignmentAxis: alignOffset }),
    ...(avoidCollisions ? [flip({ padding: collisionPadding }), shift({ padding: collisionPadding })] : []),
]

/**
 * Positions an open panel against a snapshot of where its trigger stood on opening, instead of the trigger, so
 * the panel stays put when what is chosen in it moves the trigger.
 */
export function useStayWhereOpened(refs: UseFloatingReturn['refs'], { enabled, open }: { enabled: boolean; open: boolean }) {
    useLayoutEffect(() => {
        const trigger = refs.domReference.current
        if (!enabled || !open || !trigger) return

        const rect = trigger.getBoundingClientRect()
        refs.setPositionReference({ getBoundingClientRect: () => rect, contextElement: trigger })
        return () => refs.setPositionReference(trigger)
    }, [enabled, open, refs])
}

/**
 * The items a panel navigates: floating-ui's registry of their elements and labels, and the row in play.
 */
export type DropdownList = {
    elementsRef: MutableRefObject<Array<HTMLElement | null>>
    labelsRef: MutableRefObject<Array<string | null>>
    /** The focused row, or in a searching panel the row the field's Enter would choose. */
    activeIndex: number | null
    setActiveIndex: (index: number | null) => void
}

type DropdownListOptions = {
    search: DropdownMenuSearch | undefined
    loop: boolean
    /** A submenu's list, which hands ArrowLeft and ArrowRight to the menu it opened from. */
    nested?: boolean
    /** Off for a panel that is not a menu, whose contents handle their own keys. */
    enabled?: boolean
}

/**
 * Arrow-key navigation and typeahead over a panel's items. Returns the list the panel renders its items
 * into, and the interactions to pass to `useInteractions`.
 */
export function useDropdownList(context: FloatingContext, { search, loop, nested = false, enabled = true }: DropdownListOptions) {
    const [activeIndex, setActiveIndex] = useState<number | null>(null)
    const elementsRef = useRef<Array<HTMLElement | null>>([])
    const labelsRef = useRef<Array<string | null>>([])

    const listNavigation = useListNavigation(context, {
        enabled,
        listRef: elementsRef,
        activeIndex,
        onNavigate: setActiveIndex,
        // A filtered list is short, and jumping from its end to its start reads as a jump to nowhere.
        loop: loop && !search,
        nested,
        // The caret stays in a search field while the arrow keys and the pointer mark rows, so focus
        // never leaves the field mid-word.
        virtual: !!search,
        // 'auto' means the first item is focused when the menu is opened from the keyboard and not when it
        // is clicked open: a highlighted row after a click reads as a choice already made. A search field
        // always marks its first row, so Enter takes the obvious result.
        focusItemOnOpen: search ? true : 'auto',
    })
    // Typing in a search field filters the list instead of jumping through it.
    const typeahead = useTypeahead(context, {
        enabled: enabled && !search,
        listRef: labelsRef,
        activeIndex,
        onMatch: context.open ? setActiveIndex : undefined,
    })

    const list: DropdownList = { elementsRef, labelsRef, activeIndex, setActiveIndex }
    return { list, interactions: [listNavigation, typeahead] }
}
